/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { IPreviewResolutionContext, PreviewResolutionContext } from "./PreviewResolutionContext";
import { defaultPreviewResolutionSettings, IPreviewResolutionSettings } from "./previewResolutions";
import { useStateProp, useValueChange } from "../ui/hooks";
import { isFunction, isNil } from "@adapptio/lib-shared";
import { useToggle, Viewport } from "@adapptio/lib-components";
import { MEDIA_RESOLUTIONS_default, MEDIA_RESOLUTIONS_string } from "@adapptio/lib-blueprint";
import { getMediaResolutionIdForWidth, isMediaResolutionId } from "./previewResolutionUtils";

export interface IPreviewResolutionProviderProps {
	children?: React.ReactNode;
	previewResolutionSettings?: IPreviewResolutionSettings;
	onPreviewResolutionSettingsChange?: (previewResolutionSettings: IPreviewResolutionSettings) => void;
}

export const PreviewResolutionProvider: React.FC<IPreviewResolutionProviderProps> = ({
	children,
	previewResolutionSettings: previewResolutionSettingsProp,
	onPreviewResolutionSettingsChange
}) => {
	const [ previewWidth, setPreviewWidth ] = useState(0);
	const [ previewScale, setPreviewScale ] = useState(100);
	const [ safeAreaActive, toggleSafeArea ] = useToggle(false);
	const [ previewResolutionSettings, setPreviewResolutionSettings ] =
		useStateProp<IPreviewResolutionSettings>(previewResolutionSettingsProp);
	const previewResolutionId = previewResolutionSettings?.selectedPreviewResolutionId;
	const manualWidth = previewResolutionSettings?.manualWidths[previewResolutionId];

	const setPreviewResolutionId = useCallback<IPreviewResolutionContext["setPreviewResolutionId"]>(
		(newPreviewResolutionId) => {
			setManualWidth(null);

			const newSettings: IPreviewResolutionSettings = {
				...previewResolutionSettings,
				selectedPreviewResolutionId: newPreviewResolutionId
			};

			if (isFunction(onPreviewResolutionSettingsChange)) {
				onPreviewResolutionSettingsChange(newSettings);
				return;
			}

			setPreviewResolutionSettings(newSettings);
		},
		[ previewResolutionSettings, onPreviewResolutionSettingsChange ]
	);

	const mediaResolutionId = useMemo<MEDIA_RESOLUTIONS_string>(() => {
		if (!isNil(manualWidth)) {
			return getMediaResolutionIdForWidth(manualWidth);
		}

		if (isMediaResolutionId(previewResolutionId)) {
			return previewResolutionId;
		}

		if (previewWidth === 0) {
			return MEDIA_RESOLUTIONS_default;
		}

		return getMediaResolutionIdForWidth(previewWidth);
	}, [ previewResolutionId, previewWidth, manualWidth ]);

	const setManualWidth = useCallback<IPreviewResolutionContext["setManualWidth"]>(
		(manualWidth) => {
			const selectedPreviewId = isNil(manualWidth)
				? previewResolutionId
				: getMediaResolutionIdForWidth(manualWidth);

			const newSettings: IPreviewResolutionSettings = {
				...previewResolutionSettings,
				selectedPreviewResolutionId: selectedPreviewId,
				manualWidths: {
					...(previewResolutionSettings?.manualWidths || {}),
					[selectedPreviewId]: manualWidth
				}
			};

			if (isFunction(onPreviewResolutionSettingsChange)) {
				onPreviewResolutionSettingsChange(newSettings);
				return;
			}

			setPreviewResolutionSettings(newSettings);
		},
		[ previewResolutionId, previewResolutionSettings, onPreviewResolutionSettingsChange ]
	);

	useValueChange(`${previewResolutionId}-${safeAreaActive}`, () => {
		window?.dispatchEvent(
			new CustomEvent(Viewport.UPDATE_EVENT_TYPE, { detail: { previewResolutionId } })
		);
	});

	return (
		<PreviewResolutionContext.Provider
			value={{
				previewResolutionId: mediaResolutionId,
				setPreviewResolutionId,
				mediaResolutionId,
				previewWidth,
				setPreviewWidth,
				previewScale,
				setPreviewScale,
				manualWidth,
				setManualWidth,
				safeAreaActive,
				toggleSafeArea
			}}
		>
			{children}
		</PreviewResolutionContext.Provider>
	);
};

PreviewResolutionProvider.defaultProps = {
	previewResolutionSettings: defaultPreviewResolutionSettings
};
